import { dioptre, formatDioptre, toNumber } from '@lensmira/domain';
/**
 * What a single cell of the prescription grid holds, and the rules for changing it.
 *
 * Pure, and separated from the component on purpose: this is where the sign, the 0.25 step and the
 * `AI-001` acceptance gate live, and every one of those is a correctness rule rather than a
 * rendering concern. A component test can tell you a keystroke reached a handler. Only this can
 * tell you the handler was right.
 */
/**
 * The em dash `RX-04` requires for an empty cell.
 *
 * Written as an escape rather than as the character. The repo rule is that em dashes are not used
 * in code or prose; this one is neither, it is a value the design system mandates for display, and
 * an escape keeps that distinction visible instead of looking like the rule was forgotten.
 */
export const EMPTY_MARK = '\u2014';
export const EMPTY = { kind: 'empty' };
export function entered(raw) {
    return { kind: 'entered', raw };
}
/** A value Mira suggests. It renders distinctly (`RX-09`) and cannot be committed as it stands. */
export function proposed(raw, source) {
    return { kind: 'proposed', raw, source };
}
/**
 * A human accepting a proposal. The only route from `proposed` to a value that can be stored.
 *
 * Takes the proposal by value and returns a new one, so acceptance is an explicit act at a call
 * site rather than a mutation somebody can perform by accident.
 */
export function accept(value) {
    return value.kind === 'proposed' ? { kind: 'entered', raw: value.raw } : value;
}
export function rawText(value) {
    return value.kind === 'empty' ? '' : value.raw;
}
/** §9's columns, left to right, and the tab order within one eye's row. */
export const COLUMNS = ['sphere', 'cylinder', 'axis', 'add', 'prism', 'base'];
export const COLUMN_KIND = {
    sphere: 'power',
    cylinder: 'power',
    axis: 'axis',
    add: 'power',
    prism: 'power',
    base: 'base',
};
/** §9's row order. OD is above OS and `RX-08` keeps it there in RTL. */
export const ROWS = ['od', 'os'];
/** `RX-06`: "Row labels show abbreviation and word: `od / right`. New staff do not know the Latin." */
export const ROW_LABEL = {
    od: { abbreviation: 'od', word: 'right' },
    os: { abbreviation: 'os', word: 'left' },
};
export const BASE_DIRECTIONS = ['in', 'out', 'up', 'down'];
// ---------------------------------------------------------------------------------------------
// Reading a cell
// ---------------------------------------------------------------------------------------------
/**
 * The committed dioptric value of a cell, or undefined.
 *
 * **Only reads `entered`.** A `proposed` cell returns undefined however well formed its text is,
 * which is `AI-001` enforced by the type checker rather than by a reviewer noticing.
 */
export function committedDioptre(value) {
    if (value.kind !== 'entered')
        return undefined;
    const trimmed = value.raw.trim();
    if (trimmed === '')
        return undefined;
    const n = Number(trimmed);
    if (!Number.isFinite(n))
        return undefined;
    try {
        return dioptre(n);
    }
    catch {
        return undefined;
    }
}
export function committedAxis(value) {
    if (value.kind !== 'entered')
        return undefined;
    const n = Number(value.raw.trim());
    return Number.isInteger(n) && n >= 1 && n <= 180 ? n : undefined;
}
/**
 * What the cell shows when it does not have focus.
 *
 * `RX-04`: "Empty is an em dash, never blank. Blank is indistinguishable from unsaved." And
 * `RX-03`: the sign is always rendered, including the plus, which is why a well-formed power is
 * reformatted here rather than echoed back as typed.
 */
export function displayText(value, kind) {
    const raw = rawText(value).trim();
    if (raw === '')
        return EMPTY_MARK;
    if (kind !== 'power')
        return raw;
    const d = value.kind === 'empty' ? undefined : safeDioptre(raw);
    return d === undefined ? raw : formatDioptre(d);
}
function safeDioptre(raw) {
    const n = Number(raw);
    if (!Number.isFinite(n))
        return undefined;
    try {
        return dioptre(n);
    }
    catch {
        return undefined;
    }
}
// ---------------------------------------------------------------------------------------------
// Changing a cell by keyboard
// ---------------------------------------------------------------------------------------------
/** `RX-07`: "arrows step 0.25". A power steps by a quarter dioptre; an axis by one degree. */
const POWER_STEP = 0.25;
const AXIS_STEP = 1;
/** Shift is the coarse step. Not in the spec, and it costs nothing to have; see the ADR. */
const POWER_COARSE = 1;
const AXIS_COARSE = 10;
export function step(value, kind, direction, coarse) {
    if (kind === 'base')
        return value;
    // Stepping an unaccepted proposal would silently make it an entered value, which is exactly the
    // hole AI-001 closes. The caller accepts first.
    if (value.kind === 'proposed')
        return value;
    if (kind === 'axis') {
        const current = Number(rawText(value).trim());
        const from = Number.isFinite(current) && current !== 0 ? current : 0;
        const next = from + direction * (coarse ? AXIS_COARSE : AXIS_STEP);
        // Appendix I gives axis as 1 to 180, and it is a meridian, so it wraps rather than clamping:
        // stepping down from 1 reaches 180, which is the adjacent meridian and not an error.
        const wrapped = ((((next - 1) % 180) + 180) % 180) + 1;
        return entered(String(wrapped));
    }
    const current = safeDioptre(rawText(value).trim());
    const from = current === undefined ? 0 : toNumber(current);
    const next = from + direction * (coarse ? POWER_COARSE : POWER_STEP);
    // Rounding to the grid rather than trusting float addition: 0.1 + 0.2 arithmetic has no place
    // anywhere near a dioptre, which is why Dioptre is an integer count of quarters in the first place.
    const snapped = Math.round(next * 4) / 4;
    return entered(snapped.toFixed(2));
}
/**
 * `RX-07`: "one key toggles sign". `RXW-002` lists a sign toggle key and neither names which key.
 *
 * Chosen here rather than left blank, because a grid with no toggle key does not meet the
 * requirement: **the spacebar**. It is one key, it is the largest target on the keyboard, it has no
 * meaning inside a numeric cell so nothing is lost, and it collides with no browser or screen
 * reader binding in a text input (`KB-03`). Typing `-` or `+` sets the sign directly and is the
 * faster path when entering a value from scratch; the toggle is for correcting one already there.
 */
export function toggleSign(value, kind) {
    if (kind !== 'power')
        return value;
    if (value.kind === 'proposed')
        return value;
    const raw = rawText(value).trim();
    if (raw === '')
        return value;
    const d = safeDioptre(raw);
    if (d === undefined) {
        return entered(raw.startsWith('-') ? raw.slice(1) : `-${raw}`);
    }
    return entered((-toNumber(d)).toFixed(2));
}
/**
 * `RXW-002`: "copy OD to OS".
 *
 * Copies the entered values only. A proposal is not copied, because copying one would create a
 * second unaccepted value from a single act the human never saw, and `AI-001` requires acceptance
 * per value.
 */
export function copyRow(from) {
    const out = {};
    for (const column of COLUMNS) {
        const value = from[column];
        out[column] = value.kind === 'entered' ? entered(value.raw) : EMPTY;
    }
    return out;
}
/**
 * The tab order, and the one place the two specifications disagree.
 *
 * `RXW-002` (PRD §6.3, `MUST`): "tab order **OD→OS by field**."
 * `RX-07` (`DESIGN-TOKENS.md` §9, `MUST`): "Tab **across each eye's row then down**."
 *
 * Those are opposite orders. By field is sphere OD, sphere OS, cylinder OD, cylinder OS. Across the
 * row is sphere OD, cylinder OD, axis OD, then the whole OS row.
 *
 * Resolved in favour of the PRD, because `CLAUDE.md` names it the authority and `RXW-002` is where
 * release gate 1 is cited from. It is also the safer of the two for the thing `RX-03` is worried
 * about: with both eyes' spheres adjacent, a dispenser typing -2.25 then -2.50 sees them next to
 * each other, and a transposed pair is visible at the moment it is typed rather than at collection.
 *
 * It is the worse of the two for reading off paper, where a prescription is written one eye per
 * line. That is a real cost and it is exactly the kind of thing M3 exists to correct: recorded in
 * `PROGRESS.md` for the pilot shop rather than settled here for good.
 */
export const TAB_ORDER = COLUMNS.flatMap((column) => ROWS.map((row) => ({ row, column })));
export function nextCell(current, direction) {
    const index = TAB_ORDER.findIndex((c) => c.row === current.row && c.column === current.column);
    if (index === -1)
        return undefined;
    return TAB_ORDER[index + direction];
}
export function cellId(row, column) {
    return `rx-${row}-${column}`;
}
//# sourceMappingURL=rx-value.js.map